import {once} from 'lodash';
import React, {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import {getDatadogRum} from '../../util/datadog';
import {TileMedia, TileMediaSpec, TileMediaTracking} from '../MediaCard';
import {
  defaultState as defaultPanelState,
  MediaBrowserPanelTrackingContextState,
  useMediaBrowserPanelTrackingContext,
  useMediaBrowserPanelTrackingContextMaybe,
} from './MediaBrowserPanelTrackingContext';
import {
  calculateTotalDuration,
  calculateTrackingStages,
} from './utils/tracking';

const DD_RUM_METRIC = 'mediaBrowserPanelTileTracking';
const DD_RUM_DESCRIPTION = 'Media browser panel tile load duration';

const EmptyTileMedia: TileMedia = {
  mediaKey: '',
  step: 0,
  index: 0,
  historyRow: {},
  blob: new Blob(),
  objectURL: '',
  filePath: '',
  directURL: '',
  mediaSliderKey: '',
  mediaSliderKeyValue: 0,
};

type MediaBrowserPanelTileTrackingContextType = {
  state: RefObject<MediaBrowserPanelTileTrackingContextState>;
  setAvailableFilesReceivedMs: (value: number) => void;
  setDirectUrlReceivedMs: (value: number) => void;
  setDataCachedMs: (value: number) => void;
  setMediaLoadedMs: (value: number) => void;
  setMediaLoadedSuccess: (value: boolean) => void;
  setTileMedia: (value: TileMedia) => void;
  trackSuccess: (
    tileMedia: TileMedia | null,
    tileMediaSpec: TileMediaSpec | undefined,
    getMediaTracking?: (
      tileMediaSpec: TileMediaSpec
    ) => TileMediaTracking | null
  ) => void;
  trackError: (
    tileMedia: TileMedia | null,
    tileMediaSpec: TileMediaSpec | undefined,
    getMediaTracking?: (
      tileMediaSpec: TileMediaSpec
    ) => TileMediaTracking | null
  ) => void;
  emit: () => void;
  isMediaBrowserPanelTrackingSupported: boolean;
};

export type MediaBrowserPanelTileTrackingContextState = {
  runName: string;
  mediaKey: string;
  componentCreatedMs: number;
  availableFilesReceivedMs: number;
  directUrlReceivedMs: number;
  dataCachedMs: number;
  mediaLoadedMs: number;
  mediaLoadedSuccess: boolean;
  panel: MediaBrowserPanelTrackingContextState;
  media: {
    dimensions: {
      width: number;
      height: number;
    };
    index: number;
    mime: string;
    path: string;
    size: number;
    step: number;
    stepKey: string;
    stepKeyValue: number;
    type: string;
  };
};

export const MediaBrowserPanelTileTrackingContext =
  createContext<MediaBrowserPanelTileTrackingContextType | null>(null);

export const useMediaBrowserPanelTileTrackingContext = () => {
  const context = useContext(MediaBrowserPanelTileTrackingContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserPanelTileTrackingContext must be used within a MediaBrowserPanelTileTrackingContextProvider'
    );
  }
  return context;
};

export const useMediaBrowserPanelTileTrackingContextMaybe: () => MediaBrowserPanelTileTrackingContextType =
  () => {
    const context = useContext(MediaBrowserPanelTileTrackingContext);
    if (!context) {
      // Fallback object with no-op functions and a dummy ref
      return {
        state: {
          current: {
            runName: '',
            mediaKey: '',
            componentCreatedMs: 0,
            availableFilesReceivedMs: 0,
            directUrlReceivedMs: 0,
            dataCachedMs: 0,
            mediaLoadedMs: 0,
            mediaLoadedSuccess: false,
            panel: defaultPanelState,
            media: {
              dimensions: {width: 0, height: 0},
              index: 0,
              mime: '',
              path: '',
              size: 0,
              step: 0,
              stepKey: '',
              stepKeyValue: 0,
              type: '',
            },
          },
        },
        setAvailableFilesReceivedMs: () => {},
        setDirectUrlReceivedMs: () => {},
        setDataCachedMs: () => {},
        setMediaLoadedMs: () => {},
        setMediaLoadedSuccess: () => {},
        setTileMedia: () => {},
        trackSuccess: () => {},
        trackError: () => {},
        emit: () => {},
        isMediaBrowserPanelTrackingSupported: false,
      };
    }
    return context;
  };

export type MediaBrowserPanelTileTrackingContextProviderProps = {
  children: React.ReactNode;
  mediaKey: string;
  panelId: string;
  runName: string;
};

const buildMediaFromTile = (tile: TileMedia, mediaKey: string) => {
  return {
    dimensions: {
      width: tile.historyRow?.[mediaKey]?.width ?? 0,
      height: tile.historyRow?.[mediaKey]?.height ?? 0,
    },
    index: tile.index ?? 0,
    mime: tile.historyRow?.[mediaKey]?.format ?? '',
    path: tile.filePath ?? '',
    size: tile.historyRow?.[mediaKey]?.size ?? 0,
    step: tile.step ?? 0,
    stepKey: tile.mediaSliderKey ?? '',
    stepKeyValue: tile.mediaSliderKeyValue ?? 0,
    type: tile.historyRow?.[mediaKey]?._type ?? '',
  };
};

export const MediaBrowserPanelTileTrackingContextProvider = React.memo(
  ({
    children,
    panelId,
    mediaKey,
    runName,
  }: MediaBrowserPanelTileTrackingContextProviderProps) => {
    const {
      isMediaBrowserPanelTrackingEnabled,
      isMediaBrowserPanelTrackingDebugEnabled,
      isMediaBrowserPanelTrackingSupported,
      state: panelState,
    } = useMediaBrowserPanelTrackingContext();

    // Combine all mutable state into a single ref
    const stateRef = useRef<MediaBrowserPanelTileTrackingContextState>({
      runName,
      mediaKey,
      componentCreatedMs: Date.now(),
      availableFilesReceivedMs: 0,
      directUrlReceivedMs: 0,
      dataCachedMs: 0,
      mediaLoadedMs: 0,
      mediaLoadedSuccess: false,
      panel: {
        ...defaultPanelState,
        ...panelState,
        startTimeMs: panelState.startTimeMs || Date.now(),
      },
      media: buildMediaFromTile(EmptyTileMedia, mediaKey),
    });

    const loggingPrefix = useRef(
      `MediaBrowserPanelTracking ${panelId} tile ${mediaKey}`
    );

    const log = useCallback(
      (event: string, msg: any) => {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          console.log(loggingPrefix.current, event, JSON.stringify(msg));
        }
      },
      [isMediaBrowserPanelTrackingDebugEnabled]
    );

    const getDuration = useCallback(() => {
      return calculateTotalDuration(stateRef.current);
    }, []);

    const getVital = useCallback(() => {
      // ensure updated panel state prior to building vital
      if (!stateRef.current) {
        return null;
      }
      stateRef.current.panel = panelState;
      const duration = getDuration();
      if (duration === null) {
        return null;
      }

      const stages = calculateTrackingStages(stateRef.current);
      if (!stages) {
        return null;
      }

      const ddContext = {
        ...stateRef.current,
        stages,
      };

      return {
        context: ddContext,
        description: DD_RUM_DESCRIPTION,
        duration,
        startTime: stateRef.current.componentCreatedMs,
      };
    }, [getDuration, panelState]);

    const emit = useCallback(() => {
      if (!isMediaBrowserPanelTrackingEnabled) {
        return;
      }

      const ddContext = getVital();
      // Only emit if we have a valid vital with duration
      if (!ddContext) {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          log('emit_skipped', 'No valid duration available');
        }
        return;
      }

      try {
        const dd_rum = getDatadogRum();
        dd_rum.addDurationVital(DD_RUM_METRIC, ddContext);
      } catch (error) {
        console.error('Error adding duration vital', error);
      } finally {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          log('emit', ddContext);
        }
      }
    }, [
      isMediaBrowserPanelTrackingEnabled,
      isMediaBrowserPanelTrackingDebugEnabled,
      log,
      getVital,
    ]);

    const setAvailableFilesReceivedMs = useCallback((value: number) => {
      stateRef.current.availableFilesReceivedMs = value;
    }, []);

    const setDirectUrlReceivedMs = useCallback((value: number) => {
      stateRef.current.directUrlReceivedMs = value;
    }, []);

    const setDataCachedMs = useCallback((value: number) => {
      stateRef.current.dataCachedMs = value;
    }, []);

    const setMediaLoadedMs = useCallback((value: number) => {
      stateRef.current.mediaLoadedMs = value;
    }, []);

    const setMediaLoadedSuccess = useCallback(
      (value: boolean) => {
        stateRef.current.mediaLoadedSuccess = value;
        emit();
      },
      [emit]
    );

    const setTileMedia = useCallback(
      (value: TileMedia) => {
        stateRef.current.media = buildMediaFromTile(value, mediaKey);
      },
      [mediaKey]
    );
    const noOp = useCallback(() => {}, []);

    const tracking = useMediaBrowserPanelTrackingContextMaybe();
    const {incrementCompleted} = tracking;

    const trackCompleted = useCallback(
      (
        success: boolean,
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => {
        if (!isMediaBrowserPanelTrackingEnabled || tileMedia === null) {
          return;
        }
        setTileMedia(tileMedia);
        incrementCompleted();
        const tracking = tileMediaSpec
          ? getMediaTracking?.(tileMediaSpec)
          : null;
        if (tracking) {
          const {availableFilesReceivedMs, directUrlReceivedMs, dataCachedMs} =
            tracking;
          setAvailableFilesReceivedMs(availableFilesReceivedMs ?? 0);
          setDirectUrlReceivedMs(directUrlReceivedMs ?? 0);
          setDataCachedMs(dataCachedMs ?? 0);
        }
        setMediaLoadedMs(Date.now());
        setMediaLoadedSuccess(success);
      },
      [
        setTileMedia,
        incrementCompleted,
        isMediaBrowserPanelTrackingEnabled,
        setAvailableFilesReceivedMs,
        setDirectUrlReceivedMs,
        setDataCachedMs,
        setMediaLoadedMs,
        setMediaLoadedSuccess,
      ]
    );
    const trackSuccess = useCallback(
      (
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => trackCompleted(true, tileMedia, tileMediaSpec, getMediaTracking),
      [trackCompleted]
    );
    const trackError = useCallback(
      (
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => trackCompleted(false, tileMedia, tileMediaSpec, getMediaTracking),
      [trackCompleted]
    );

    const contextValue = React.useMemo(
      () => ({
        state: stateRef,
        setAvailableFilesReceivedMs: isMediaBrowserPanelTrackingSupported
          ? once(setAvailableFilesReceivedMs)
          : noOp,
        setDirectUrlReceivedMs: isMediaBrowserPanelTrackingSupported
          ? once(setDirectUrlReceivedMs)
          : noOp,
        setDataCachedMs: isMediaBrowserPanelTrackingSupported
          ? once(setDataCachedMs)
          : noOp,
        setMediaLoadedMs: isMediaBrowserPanelTrackingSupported
          ? once(setMediaLoadedMs)
          : noOp,
        setMediaLoadedSuccess: isMediaBrowserPanelTrackingSupported
          ? once(setMediaLoadedSuccess)
          : noOp,
        setTileMedia: isMediaBrowserPanelTrackingSupported
          ? once(setTileMedia)
          : noOp,
        trackSuccess: isMediaBrowserPanelTrackingSupported
          ? once(trackSuccess)
          : noOp,
        trackError: isMediaBrowserPanelTrackingSupported
          ? once(trackError)
          : noOp,
        emit,
        isMediaBrowserPanelTrackingSupported,
      }),
      [
        isMediaBrowserPanelTrackingSupported,
        setAvailableFilesReceivedMs,
        setDirectUrlReceivedMs,
        setDataCachedMs,
        setMediaLoadedMs,
        setMediaLoadedSuccess,
        setTileMedia,
        trackSuccess,
        trackError,
        emit,
        noOp,
      ]
    );

    return (
      <MediaBrowserPanelTileTrackingContext.Provider value={contextValue}>
        {children}
      </MediaBrowserPanelTileTrackingContext.Provider>
    );
  }
);

export function useGetTrackingCallbacks(
  tileMedia: TileMedia | null,
  tileMediaSpec: TileMediaSpec | undefined,
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null
) {
  const {trackSuccess, trackError} =
    useMediaBrowserPanelTileTrackingContextMaybe();
  const trackSuccessCallback = useCallback(() => {
    return trackSuccess(tileMedia, tileMediaSpec, getMediaTracking);
  }, [trackSuccess, tileMedia, tileMediaSpec, getMediaTracking]);
  const trackErrorCallback = useCallback(() => {
    return trackError(tileMedia, tileMediaSpec, getMediaTracking);
  }, [trackError, tileMedia, tileMediaSpec, getMediaTracking]);
  return useMemo(
    () => ({
      trackSuccess: trackSuccessCallback,
      trackError: trackErrorCallback,
    }),
    [trackSuccessCallback, trackErrorCallback]
  );
}
